import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    LinearProgress,
    Typography,
    CircularProgress,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface ProgressModalProps {
    open: boolean;
    title: string;
    progress: number; // 0-100
    status: 'preparing' | 'processing' | 'completed' | 'error';
    message?: string;
    onClose?: () => void;
}

const ProgressModal = ({ open, title, progress, status, message, onClose }: ProgressModalProps) => {
    const { t } = useTranslation();

    const getStatusColor = () => {
        switch (status) {
            case 'preparing':
                return '#2196f3'; // Blue
            case 'processing':
                return '#4caf50'; // Green
            case 'completed':
                return '#4caf50'; // Green
            case 'error':
                return '#f44336'; // Red
            default:
                return '#2196f3';
        }
    };

    const getStatusMessage = () => {
        if (message) return message;

        switch (status) {
            case 'preparing':
                return t('progress.preparing');
            case 'processing':
                return t('progress.processing');
            case 'completed':
                return t('progress.completed');
            case 'error':
                return t('progress.error');
            default:
                return '';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={status === 'completed' ? onClose : undefined}
            maxWidth="xs"
            disableEscapeKeyDown={status !== 'completed' && status !== 'error'}
            aria-labelledby="progress-dialog-title"
            aria-describedby="progress-dialog-description"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        padding: 0.5,
                        minWidth: 280,
                        maxWidth: 320,
                    },
                },
            }}
        >
            <DialogTitle id="progress-dialog-title" sx={{ textAlign: 'center', pb: 0.5, pt: 1 }}>
                <Typography variant="subtitle1" component="div">
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box
                    id="progress-dialog-description"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 1,
                        px: 1,
                    }}
                >
                    {/* Progress Indicator */}
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                        {status === 'completed' ? (
                            <CheckCircle
                                sx={{
                                    fontSize: 48,
                                    color: getStatusColor(),
                                }}
                                aria-label={t('progress.completed')}
                            />
                        ) : (
                            <>
                                <CircularProgress
                                    variant="determinate"
                                    value={progress}
                                    size={48}
                                    thickness={4}
                                    sx={{
                                        color: getStatusColor(),
                                    }}
                                    aria-label={`${Math.round(progress)}% complete`}
                                />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        color="textSecondary"
                                        sx={{ fontSize: '0.65rem', fontWeight: 'bold' }}
                                        aria-hidden="true"
                                    >
                                        {`${Math.round(progress)}%`}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Linear Progress Bar */}
                    {status !== 'completed' && (
                        <Box sx={{ width: '100%' }}>
                            <progress
                                value={progress}
                                max={100}
                                style={{
                                    width: '100%',
                                    height: '6px',
                                    appearance: 'none',
                                    WebkitAppearance: 'none',
                                    borderRadius: '3px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                }}
                            />
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'transparent',
                                    position: 'absolute',
                                    width: '100%',
                                    marginTop: '-6px',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getStatusColor(),
                                        borderRadius: 3,
                                    },
                                }}
                            />
                        </Box>
                    )}

                    {/* Status Message */}
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                            textAlign: 'center',
                            fontWeight: status === 'error' ? 'bold' : 'normal',
                            color: status === 'error' ? getStatusColor() : 'textSecondary',
                            fontSize: '0.875rem',
                        }}
                    >
                        {getStatusMessage()}
                    </Typography>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ProgressModal;
